import { ApplicationRef, ComponentFactoryResolver, EmbeddedViewRef, Inject, Injectable, Injector } from '@angular/core';
import { SquirtResultsComponent } from '@finder/shared/features/squirt/squirt-results/squirt-results.component';
import { SquirtData, SquirtMediaDetails, SquirtMediaSizes } from '@finder/shared/features/squirt/squirt.interface';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';

/**
 * Constants calculated from finding the averages among the range of averages and standard deviations
 * for tiny, small, medium, and large images.
 * Source: https://docs.google.com/spreadsheets/d/17-RVR7QWemptHN44ov1J9P_Fmaq2L8jwO6zQ4V6hmPo/edit?pli=1#gid=0
 * Converted to kB
 * */

const TINY_AVG = 2.802;
const TINY_STDEV = 0.367;
const SMALL_AVG = 14.659;
const SMALL_STDEV = 2.011;
const MEDIUM_AVG = 53.97;
const MEDIUM_STDEV = 9.833;
const LARGE_AVG = 251.525;
const LARGE_STDEV = 66.284;

/**
 * Assuming that the videos are either 640x360(SD) or 1280x720(HD) and ~30s long.
 * Appropriate size varies on mutliple factors like pixel size, length of video, audio, color depth, compression,
 * all of which are difficult to accurately reference.
 * Therefore current judgement is based solely on pixel size and approximate 30s length,
 * and averaging approximate estimates from various sources
 * https://www.filecatalyst.com/blog/how-big-are-movie-files/
 * https://www.circlehd.com/blog/how-to-calculate-video-file-size
 * https://web.library.uq.edu.au/library-services/it/learnuq-blackboard-help/learnuq-assessment/eportfolio/video-content-recommendations
 * Converted to kB
 */

const HD_VID_AVG = 4580;
const HD_VID_STDEV = 1390;
const SD_VID_AVG = 1000;
const SD_VID_STDEV = 50;

/**
 * Squirt service pulls images and video elements from the current document.
 * Those media are analyized for size and then styled in the browser to illustrate visually their size.
 */
@Injectable({
    providedIn: 'root',
})
export class SquirtService {
    mediaData: SquirtData = {
        totalMedia: 0,
        mediaBySize: {
            tiny: 0,
            small: 0,
            medium: 0,
            big: 0,
            huge: 0,
            total: 0,
        } as SquirtMediaSizes,
        mediaDetails: [] as SquirtMediaDetails[]
    };

    private moveInterval;
    private processingDiv;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector,
        private windowRef: WindowRef,
    ) {}

    async checkImages(doc: Document) {
        // ensure all lazy-loaded images are loaded by scrolling to the bottom of the page before analyzing
        // taking it in chunks to ensure the lazy load has time to work
        await this.move(doc, 1500, 7, doc.body.scrollHeight/6);

        // Clear interval
        clearInterval(this.moveInterval);

        new Promise<void>((resolve, reject) => {
            // get an HTML collection of all images, convert to an array
            const allImages = Array.from(doc.images);
            // can't forget cinemagraphs, convert to an array
            const allCinemagraphs = Array.from(doc.getElementsByTagName('video'));

            // combine images and cinemagraphs together
            const allMedia = [].concat(allImages, allCinemagraphs);
            // not all media are equal, so check them first
            allMedia.forEach((media) => {
                const mediaSrc = media.attributes.src.value;
                // filter out any data images
                if (!mediaSrc.includes('data:image') && !mediaSrc.includes('data:video')) {
                    // media is an instance of a NamedNodeMap, which can be used to access its attributes

                    const request = new XMLHttpRequest();
                    request.open('GET', mediaSrc, true);
                    request.responseType = 'blob';  // specify how the response should be treated

                    request.onload = () => {
                        const mediaBlob = request.response;
                        this.readMediaOnPage(doc, mediaSrc, mediaBlob);
                    };
                    request.send();
                }
            });

            resolve();
        });

        // Remove procesisng div
        this.processingDiv.remove();

        return this.mediaData;
    }

    createComponent(doc: Document, mediaData: SquirtData) {
        // Create component
        const componentRef = this.componentFactoryResolver
            .resolveComponentFactory(SquirtResultsComponent)
            .create(this.injector);

        // Bind data
        componentRef.instance.mediaData = mediaData;

        // Add to ng tree
        this.appRef.attachView(componentRef.hostView);

        // Get DOM element
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
            .rootNodes[0] as HTMLElement;

        // Insert into DOM
        doc.body.prepend(domElem);
    }

    private readMediaOnPage = (doc: Document, mediaSrc: string, mediaBlob: Blob) => {
        // scope our tool just to Nemo itself, excluding the global nav
        const nemoRoot = doc.querySelector('.finder-content');
        const mediaElement = nemoRoot.querySelector(`[src="${mediaSrc}"]`) as HTMLMediaElement;
        if (!mediaElement) {
            // no valid image, so just bail
            return;
        }

        // Add overlay
        const overlay = doc.createElement('span');
        overlay.classList.add('squirt-overlay');
        mediaElement.parentNode.insertBefore(overlay, mediaElement);

        // a Blob's size is reported in bytes, so divide by 1000 to get kBs
        const mediaSize = (mediaBlob.size / 1000).toFixed(2) as unknown as number; // kb to 2 decimal points
        this.mediaData.totalMedia++;

        // judging media size based on displayed width in client, not on intrinsic image width
        this.highlightMediaOnPage(mediaElement.clientWidth, mediaSize, mediaElement, mediaElement.nodeName === 'VIDEO');

        // add a text display showing the size, in kb rounded to 2 decimal places
        this.addTextNode(doc, mediaSize, mediaElement);
    };

    private addTextNode = (doc: Document, mediaSize: number, mediaElement: HTMLMediaElement) => {
        const newDiv = doc.createElement('div');
        newDiv.appendChild(doc.createTextNode(`Media size: ${mediaSize} KB`));
        newDiv.classList.add('size-label');

        // Move to overlay if possible
        const overlay = mediaElement.parentNode.querySelector('.squirt-overlay');
        if (overlay) {
            overlay.appendChild(newDiv);
        } else {
            mediaElement.insertAdjacentElement('afterend', newDiv);
        }
    };

    private move = (doc: Document, delay, repetitions, scrollAmt) =>
        new Promise<void>((resolve) => {
            let reps = 0;
            let currentScroll = 0;

            // Add class to body to block pointer events
            doc.body.classList.add('noaction');

            // Add processing overlay
            this.processingDiv = doc.createElement('div');
            this.processingDiv.classList.add('squirt-processing');
            this.processingDiv.appendChild(doc.createTextNode(`Squirt is processing images...`));
            doc.body.prepend(this.processingDiv);

            this.moveInterval = setInterval( () => {
                window.scrollTo({
                    left: 0,
                    top: currentScroll,
                    behavior: 'smooth'
                });

                if (reps++ === repetitions) {
                    // Scroll to top
                    this.windowRef.nativeWindow.scrollTo({ top: 0, behavior: 'smooth' });

                    // Remove class from body
                    doc.body.classList.remove('noaction');

                    // Resolve to signal we are done
                    resolve();
                }

                currentScroll += scrollAmt;
            }, delay);
        });

    private highlightMediaOnPage(width, size: number, mediaElement: HTMLMediaElement, isVideo = false) {
        let avg;
        let stdev;

        if (isVideo) {
            avg = SD_VID_AVG;
            stdev = SD_VID_STDEV; //idk completely guessed

            /**
             * 960 is treated as border because it is inbetween size of 640 and 1280 - good is within 2.5-6.66MB range
             * stdev of 1.39(dividing the range difference by 3)
             */
            if (width >= 960) {
                avg = HD_VID_AVG;
                stdev = HD_VID_STDEV;
            }
        } else { //is image
            avg = TINY_AVG;
            stdev = TINY_STDEV;
            if (width >= 160 && width < 350) {
                avg = SMALL_AVG;
                stdev = SMALL_STDEV;
            } else if (width >= 350 && width < 750) {
                avg = MEDIUM_AVG;
                stdev = MEDIUM_STDEV;
            } else if (width >= 750) {
                avg = LARGE_AVG;
                stdev = LARGE_STDEV;
            }
        }

        // Increment size bucket counter
        let squirtSize = '';
        if (size < (avg - stdev)) { //green
            squirtSize = 'tiny';
            this.mediaData.mediaBySize.tiny++;
        } else if (size >= (avg - stdev) && size < (avg + stdev)) { //blue
            squirtSize = 'small';
            this.mediaData.mediaBySize.small++;
        } else if (size >= (avg + stdev) && size < (avg + (stdev * 2))) { //yellow
            squirtSize = 'medium';
            this.mediaData.mediaBySize.medium++;
        } else if (size >= (avg + (stdev * 2)) && size < (avg + (stdev * 3))) { //orange
            squirtSize = 'big';
            this.mediaData.mediaBySize.big++;
        } else { //red
            squirtSize = 'huge';
            this.mediaData.mediaBySize.huge++;
        }

        this.mediaData.mediaBySize.total++;

        // Add media details
        this.mediaData.mediaDetails.push({
            file: mediaElement.src,
            size,
            readableSize: size + 'KB',
            category: squirtSize
        });

        // Add css class
        this.addSquirtClass(mediaElement, `squirt-${squirtSize}`);
    };

    private addSquirtClass(node, size) {
        node.classList.add(size);
        const overlay = node.parentElement.querySelector('.squirt-overlay');
        if (overlay) {
            overlay.classList.add(size);
        }
    }
}
